/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Menu, { MenuProps } from '@mui/material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: 'rgb(55, 65, 81)',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
    ...theme.applyStyles?.('dark', {
      color: theme.palette.grey[300],
    }),
  },
}));

interface ArgosMenuProps {
  menuTitle: string;
  buttonProps?: React.ComponentProps<typeof Button>;
  children: React.ReactNode;
  menuId?: string;
}

export const ArgosMenu: React.FC<ArgosMenuProps> = ({ menuTitle, buttonProps, children, menuId }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Adds a onClick handler to each menu item to close the menu when an item is clicked.
   */
  function addCloseMenuHandlerToChildren(node: React.ReactNode): React.ReactNode {
    if (Array.isArray(node)) {
      return node.map(addCloseMenuHandlerToChildren);
    }
    if (React.isValidElement(node)) {
      const _children = node.props?.children ? addCloseMenuHandlerToChildren(node.props.children) : undefined;
      const originalOnClick = node.props.onClick;
      const disableCloseOnClick = node.props.disableCloseOnClick;

      return React.cloneElement(
        node as React.ReactElement<any>,
        {
          onClick: (event: React.MouseEvent<HTMLElement>) => {
            if (!disableCloseOnClick) {
              handleClose();
            }
            if (originalOnClick) {
              originalOnClick(event);
            }
          },
          children: _children,
        }
      );
    }
    return node;
  }

  const childrenWithCloseMenuHandler = addCloseMenuHandlerToChildren(children);

  const resolvedMenuId = menuId || "argos-menu";
  const resolvedButtonId = `${resolvedMenuId}-button`;

  return (
    <div>
      <Button
        id={resolvedButtonId}
        aria-controls={open ? resolvedMenuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="text"
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{ textTransform: 'none', fontSize: 16, ...buttonProps?.sx }}
        {...buttonProps}
      >
        {menuTitle}
      </Button>
      <StyledMenu
        id={resolvedMenuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {childrenWithCloseMenuHandler}
      </StyledMenu>
    </div>
  );
};
